import type { FC } from "react";
import { Skeleton } from "../components/Skeleton";
import type { CalendarType, CalendarViewType } from "./CalendarContext";
import "./CalendarSkeleton.css";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const LIST_ROW_KEYS = [
	"row-0",
	"row-1",
	"row-2",
	"row-3",
	"row-4",
	"row-5",
	"row-6",
	"row-7",
] as const;

type CalendarSkeletonProps = {
	view: CalendarViewType;
	type: CalendarType;
};

export const CalendarSkeleton: FC<CalendarSkeletonProps> = ({ view, type }) => {
	if (view === "list") {
		return (
			// biome-ignore lint/a11y/useSemanticElements: presentational loading placeholder
			<div
				className="calendar-skeleton-list"
				role="status"
				aria-busy="true"
				aria-label="Loading calendar"
			>
				<div className="calendar-skeleton-list-header">
					<Skeleton height={14} width="12%" />
					<Skeleton height={14} width="40%" />
					<Skeleton height={14} width="14%" />
					<Skeleton height={14} width="12%" />
				</div>
				{LIST_ROW_KEYS.map((key) => (
					<div key={key} className="calendar-skeleton-list-row">
						<Skeleton height={16} width="14%" />
						<div className="calendar-skeleton-list-content">
							<Skeleton height={14} width="55%" />
							<Skeleton height={12} width="85%" />
						</div>
						<Skeleton height={16} width="12%" />
						<Skeleton height={16} width="10%" />
					</div>
				))}
			</div>
		);
	}

	const cellCount = type === "week" ? 7 : 35;
	const dayKeys = Array.from({ length: cellCount }, (_, i) => `day-${i}`);

	return (
		// biome-ignore lint/a11y/useSemanticElements: presentational loading placeholder
		<div role="status" aria-busy="true" aria-label="Loading calendar">
			<div className="calendar-days">
				{WEEKDAYS.map((day) => (
					<span key={day}>{day}</span>
				))}
			</div>
			<div
				className={
					type === "week"
						? "calendar-week-days calendar-skeleton-week"
						: "calendar-week-days calendar-skeleton-month"
				}
			>
				{dayKeys.map((key, i) => (
					<div key={key} className="calendar-day calendar-skeleton-day">
						<Skeleton height={12} width="2.25rem" />
						<div className="calendar-skeleton-posts">
							<Skeleton height={28} width="100%" />
							{type === "month" && i % 3 !== 0 && (
								<Skeleton height={28} width="78%" />
							)}
							{type === "week" && (
								<>
									<Skeleton height={72} width="100%" />
									<Skeleton height={72} width="100%" />
								</>
							)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};
